// src/pages/UserPage/UserPage.jsx
import React, { useEffect } from 'react';
import {
  Box, Container, Typography, Paper, CircularProgress,
  Tabs, Tab, useMediaQuery, useTheme,
} from '@mui/material';
import { User, Building2, CreditCard } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { ProfileAndBillingSection } from './sections/ProfileAndBillingSection';
import { OrganizationSection } from './sections/OrganizationSection';
import { PaymentsSection } from './sections/PaymentsSection';
import { CourierBriefing } from '../../components/CourierBriefing/CourierBriefing';
import { SenderBriefing } from '../../components/SenderBriefing/SenderBriefing';
import { SenderReferralSection } from './sections/SenderReferralSection';
import { RideShareSection } from './sections/RideShareSection';   // ← add


export const UserPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const { user, loading: authLoading } = useAuth();
  const { userData, loading: userLoading } = useUser();

  const isSpecialCourier = userData?.roles?.includes('specialCourier');
  const isCourier        = userData?.roles?.includes('specialCourier') || userData?.mode === 'courier';
  const isSender         = userData?.mode === 'sender';

  const showCourierBriefing = isCourier && !userData?.briefingRead;
  const showSenderBriefing  = isSender  && !userData?.senderBriefingRead;

  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = (() => {
    if (tabParam === 'organisaatio' && isSpecialCourier) return 1;
    if (tabParam === 'maksut'       && isCourier)        return isSpecialCourier ? 2 : 1;
    return 0;
  })();

  const handleTabChange = (_, v) => {
    if (v === 0) { setSearchParams({}); return; }
    if (v === 1 && isSpecialCourier) { setSearchParams({ tab: 'organisaatio' }); return; }
    setSearchParams({ tab: 'maksut' });
  };

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
  }, [authLoading, user, navigate]);

  if (authLoading || userLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">Ladataan...</Typography>
        </Box>
      </Container>
    );
  }

  if (!user) return null;

  // Build tabs dynamically
  const tabs = [
    { label: 'Profiili', icon: <User size={18} /> },
    ...(isSpecialCourier ? [{ label: 'Organisaatio', icon: <Building2 size={18} /> }] : []),
    ...(isCourier        ? [{ label: 'Maksut',       icon: <CreditCard size={18} /> }] : []),
  ];

  const paymentsTabIndex = isSpecialCourier ? 2 : 1;

  // Banner is 36 px tall — push content down so it doesn't overlap the Paper
  const briefingOffset = (showCourierBriefing || showSenderBriefing) ? 36 : 0;

  return (
    <>
      <CourierBriefing
        user={user}
        userData={userData}
        onProfileNavigate={navigate}
      />

      <SenderBriefing
        user={user}
        userData={userData}
      />

      <Container
        maxWidth="md"
        sx={{
          pt: {
            xs: `${80 + briefingOffset}px`,
            sm: `${96 + briefingOffset}px`,
          },
          pb: { xs: 2, sm: 4 },
        }}
      >
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>

          {tabs.length > 1 && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0, sm: 2 } }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  sx={{ minHeight: { xs: 48, sm: 64 } }}
                />
              ))}
            </Tabs>
          )}

          {/* Profile */}
          <Box role="tabpanel" hidden={activeTab !== 0}>
            {activeTab === 0 && <ProfileAndBillingSection user={user} userData={userData} />}
          </Box>

          {/* Organisation */}
          {isSpecialCourier && (
            <Box role="tabpanel" hidden={activeTab !== 1}>
              {activeTab === 1 && <OrganizationSection user={user} userData={userData} />}
            </Box>
          )}

          {/* Payments */}
          {isCourier && (
            <Box role="tabpanel" hidden={activeTab !== paymentsTabIndex}>
              {activeTab === paymentsTabIndex && <PaymentsSection user={user} userData={userData} />}
            </Box>
          )}
   {/* Ride sharing — couriers only, shown on the profile tab */}
          {isCourier && activeTab === 0 && (
            <RideShareSection user={user} userData={userData} />
          )}
          {/* Sender referral — couriers only, shown on the profile tab */}
          {/* {isCourier && activeTab === 0 && (
            <SenderReferralSection user={user} userData={userData} />
          )} */}
        </Paper>
      </Container>
    </>
  );
};

export default UserPage;